import { Link } from "react-router-dom";
import { Bird, Shield, Check, Phone, Star, MapPin, Clock } from "lucide-react";
import UnifiedCalculator from "../components/UnifiedCalculator";
import { Logo } from "../components/Layout";
import { Header } from "../components/Layout";
import IpswichBreadcrumb from "../components/IpswichBreadcrumb";

const suburbs = ["Ipswich CBD", "Brassall", "Booval", "Bundamba", "Goodna", "Redbank Plains", "Collingwood Park", "Bellbird Park", "Ripley", "Springfield", "North Ipswich", "East Ipswich", "Leichhardt", "Yamanto", "Karalee"];

export default function BirdControlIpswichPage() {
  return (
    <>
      <>
        <title>Bird Control Ipswich | Solar Bird Proofing & Pigeon Removal | Pest Free</title>
        <meta name="description" content="Professional bird control in Ipswich QLD. Solar panel bird proofing, pigeon removal, bird netting & mite treatment. 10-year warranty on mesh. QBCC licensed. Get an instant quote." />
        <link rel="canonical" href="https://pestfreebrisbane.com.au/bird-control-ipswich" />
      </>

      <Header />
      <div className="min-h-screen bg-slate-50">
        <IpswichBreadcrumb current="Bird Control" />
        <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-[#5BC0DE]/20 text-white pt-28 pb-16 md:pt-32 md:pb-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#5BC0DE]/20 border border-[#5BC0DE]/30 rounded-full px-4 py-1.5 mb-5">
                  <MapPin className="w-4 h-4 text-[#5BC0DE]" />
                  <span className="text-sm font-medium text-[#5BC0DE]">Ipswich QLD</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight" data-testid="bird-control-h1">
                  Bird Control <span className="text-[#5BC0DE]">Ipswich</span>
                </h1>
                <p className="text-lg text-slate-300 mb-6">
                  From pigeons nesting under solar panels to mynas roosting on commercial buildings — we provide complete bird control solutions across Ipswich. Solar panel bird proofing with stainless steel mesh, bird netting, and professional clean-up services.
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#5BC0DE]" />10-Year Mesh Warranty</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#5BC0DE]" />QBCC Licensed</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#5BC0DE]" />Fully Insured</div>
                </div>
                <div className="flex items-center gap-3">
                  <a href="tel:0737081422" className="flex items-center gap-2 bg-[#5BC0DE] hover:bg-[#4AB0CE] text-white font-semibold px-6 py-3 rounded-full transition-all" data-testid="bird-control-call-btn">
                    <Phone className="w-5 h-5" /> (07) 3708 1422
                  </a>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                    <span className="text-sm text-slate-300 ml-1">4.9 on Google</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <Logo className="h-32 md:h-40 opacity-90" />
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16" id="calculator">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800">Get Your Bird Control Quote</h2>
              <p className="text-slate-600 mt-2">Solar panel bird proofing with instant pricing for Ipswich</p>
            </div>
            <UnifiedCalculator source="bird_control_ipswich" />
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-3">Bird Control Services We Offer in Ipswich</h2>
            <p className="text-slate-600 text-center mb-10 max-w-3xl mx-auto">Whether it's pigeons under your solar panels or birds damaging your commercial property, we have the right solution.</p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: "Solar Panel Bird Proofing", desc: "Stainless steel mesh with epoxy resin coating installed around your solar panels. Prevents pigeons and other birds from nesting underneath. 10-year warranty on mesh and installation.", icon: Bird, color: "#5BC0DE" },
                { title: "Bird Nest Removal & Clean-up", desc: "Full removal of bird nests, droppings and debris from under solar panels, in roof voids and on building exteriors. Includes gutter cleaning and panel washing options.", icon: Shield, color: "#7ABC32" },
                { title: "Bird Mite Treatment", desc: "Bird mites from pigeon nests can enter your home through the roof. We provide specialist chemical treatment to eliminate bird mites from roof voids and living areas.", icon: Shield, color: "#E74C3C" },
              ].map((item, i) => (
                <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: item.color + '15' }}>
                    <item.icon className="w-6 h-6" style={{ color: item.color }} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Why Bird Control Matters in Ipswich</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: "Solar Panel Damage", desc: "Ipswich has one of the highest solar panel uptake rates in Queensland. Pigeons nesting under panels cause wiring damage, reduced panel efficiency and void manufacturer warranties.", color: "#5BC0DE" },
                { title: "Health Risks from Bird Mites", desc: "When pigeons nest under your solar panels, bird mites can migrate into your home through the roof. These tiny parasites bite humans and are notoriously difficult to eliminate without professional treatment.", color: "#E74C3C" },
                { title: "Gutter & Roof Damage", desc: "Bird droppings and nesting material block gutters, causing water overflow and potential structural damage. The acidic droppings also corrode roofing materials over time.", color: "#D2691E" },
                { title: "Property Value Impact", desc: "Visible bird damage, staining and droppings on roofs and facades reduce your property's curb appeal and can be flagged in building inspections when selling.", color: "#7ABC32" },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-5 rounded-xl bg-white border border-slate-200">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: item.color + '15' }}>
                    <Shield className="w-5 h-5" style={{ color: item.color }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">{item.title}</h3>
                    <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-6">Our Recent Bird Proofing Work in Ipswich</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/z5jjmahy_IMG_1795.jpeg", alt: "Mesh close-up on solar panels" },
                { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/uyek99ws_IMG_5432.jpeg", alt: "Bird mesh along roof edge" },
                { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/1g87oe1v_IMG_0886.png", alt: "Bird proofing on new panels" },
                { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/7avvtv1a_IMG_0716.jpeg", alt: "Cleaned panels with mesh installed" },
              ].map((p, i) => (
                <div key={i} className="rounded-xl overflow-hidden aspect-square shadow-md">
                  <img src={p.url} alt={p.alt} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Bird Control FAQ — Ipswich</h2>
            <div className="space-y-4">
              {[
                { q: "How much does solar panel bird proofing cost in Ipswich?", a: "Pricing depends on the number of solar panels, roof height and whether cleaning services are needed. Use our instant quote calculator above for a detailed estimate, or call us for a free on-site assessment." },
                { q: "What mesh do you use for bird proofing?", a: "We use professional-grade stainless steel mesh with an epoxy resin coating. It's UV-resistant, corrosion-proof and comes with a 10-year warranty on materials and installation." },
                { q: "Do you remove existing bird nests?", a: "Yes. We remove all nests, droppings and debris from under your solar panels as part of the bird proofing installation. We also offer gutter cleaning, panel washing and pressure washing as add-on services." },
                { q: "Can bird mites from pigeons get into my house?", a: "Yes — this is one of the most common issues we see in Ipswich. When pigeon nests are close to your roof, bird mites travel through gaps into your living areas. We offer specialist bird mite treatment to eliminate them from roof voids and interior spaces." },
                { q: "How long does bird proofing installation take?", a: "Most residential solar panel bird proofing jobs in Ipswich take 2-4 hours for a single-storey home. Two-storey properties or those requiring extensive cleaning may take a full day." },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-slate-200">
                  <h3 className="font-bold text-slate-800 mb-2">{item.q}</h3>
                  <p className="text-sm text-slate-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Bird Control Across Ipswich</h2>
            <div className="flex flex-wrap justify-center gap-2">
              {suburbs.map(s => (
                <span key={s} className="bg-slate-50 border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700">{s}</span>
              ))}
            </div>
            <p className="text-slate-500 text-sm mt-4">And all surrounding areas in the Ipswich region</p>
          </div>
        </section>

        <section className="py-8 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-lg font-bold text-slate-800 text-center mb-4">Related Services in Ipswich</h2>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/bird-proofing-ipswich" className="text-sm text-[#5BC0DE] hover:underline font-medium">Solar Bird Proofing Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/pest-control-ipswich" className="text-sm text-[#E74C3C] hover:underline font-medium">Pest Control Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/cockroach-treatment-ipswich" className="text-sm text-[#E74C3C] hover:underline font-medium">Cockroach Treatment Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/termite-inspection-ipswich" className="text-sm text-[#D2691E] hover:underline font-medium">Termite Inspection Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/end-of-lease-pest-control-ipswich" className="text-sm text-[#7ABC32] hover:underline font-medium">End of Lease Pest Control</Link>
            </div>
          </div>
        </section>

        <section className="py-12 bg-gradient-to-r from-slate-800 to-[#5BC0DE] text-white text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Stop Birds Damaging Your Property</h2>
            <p className="text-lg text-white/80 mb-6">Professional bird control solutions across Ipswich. 10-year warranty on solar bird proofing.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:0737081422" className="inline-flex items-center gap-2 bg-white text-slate-800 font-bold px-8 py-3 rounded-full hover:bg-slate-100 transition-all">
                <Phone className="w-5 h-5" /> Call (07) 3708 1422
              </a>
              <a href="#calculator" className="inline-flex items-center gap-2 border-2 border-white text-white font-bold px-8 py-3 rounded-full hover:bg-white/10 transition-all">
                <Clock className="w-5 h-5" /> Get Instant Quote
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
